import type { ZephyrResponse } from '../../types';
import type { FirmwareUpdateResponse } from '../../types/communication';


// ----------------------------------------------------------------------------
// FSMService.ts
// ----------------------------------------------------------------------------

import { Directory, Filesystem } from '@capacitor/filesystem';
import * as Sentry from '@sentry/react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import _, { identity, isError } from 'lodash';
import {
  IObservableValue,
  IValueDidChange,
  Lambda,
  observable,
  toJS,
  action,
  runInAction,
  makeAutoObservable,
  observe,
} from 'mobx';
import { Instance } from 'mobx-state-tree';
import { ReplaySubject, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import {
  FirmwareUpdateAvailable,
  FirmwareUpToDate,
} from '../../types/communication';

import { appData } from 'models/AppData';
import { SERVER_URL_ALPHA } from 'util/constants';
import {
  BluetoothEntry,
  BluetoothInfo,
  BluetoothInfoModel,
} from '../../models/BluetoothInfo';
import { DebugStore } from '../../models/DebugStore/DebugStore';
import * as Pck from '../../spinnaxBase/logic/Com_BLE_packets';
import { numberArrayToString } from '../../spinnaxBase/logic/utilities';
import {
  BLCmds,
  BLCmdType,
  BLEDongleState,
  BLRsp,
  BLRsps,
  COM,
  ST,
  _LogLevel,
} from '../../types';
import { blobToBase64, makeErrorToast, withTimeout } from '../../util';
import BluetoothLEService, { StatesBLE } from '../BLE/BLE';
import { generateContext } from '../utils';
import { Signal } from '../../util/signal';
import { StatesUserInput } from 'pages/Record';
import { logger, LogLevel } from '../Logger';
import { t } from 'i18next';

// Firmware-Update-Status für persistentes Modal
export type FirmwareUpdateStatus = {
  fileName: string;
  status:
    | 'pending'
    | 'initializing'
    | 'uploading'
    | 'validating'
    | 'installing'
    | 'completed'
    | 'failed';
  progress?: number;
  message?: string;
  startTime?: Date;
  endTime?: Date;
  errorDetails?: string;
};

// Firmware-Update-History Typ
export type FirmwareUpdateHistoryEntry = FirmwareUpdateStatus;

// ----------------------------------------------------------------------------
// Typedefs and statemachine transition definitions
// ----------------------------------------------------------------------------

const CmdKind: BLCmds = { ...Pck };

export enum FSMState {
  Idle = 'IDLE',
  Busy = 'BUSY',
  Record = 'RECORD',
  Play = 'PLAY',
  Maintain = 'MAINTAIN',
  Tare = 'TARE',
}

const allowedTransitions: Record<
  FSMState,
  Partial<Record<FSMState, BLCmdType>>
> = {
  IDLE: {
    RECORD: CmdKind.PckCmdReBeg,
    PLAY: CmdKind.PckCmdPlBeg,
    MAINTAIN: CmdKind.PckCmdMaBeg,
  },
  BUSY: { IDLE: CmdKind.PckCmdIdBeg },
  RECORD: { IDLE: CmdKind.PckCmdIdBeg },
  PLAY: { IDLE: CmdKind.PckCmdIdBeg },
  MAINTAIN: { IDLE: CmdKind.PckCmdIdBeg },
  TARE: {},
};

const msgToState: Partial<Record<keyof BLRsps, FSMState>> = {
  PckRspIdBeg: FSMState.Idle,
  PckRspReBeg: FSMState.Record,
  PckRspMaBeg: FSMState.Maintain,
  PckRspPlBeg: FSMState.Play,
};

const stateToCmdName: Partial<Record<FSMState, keyof BLCmds>> = _.transform(
  msgToState,
  (result, value, key) => {
    result[value] = key.replace('Rsp', 'Cmd') as keyof BLCmds;
  },
  {} as Partial<Record<FSMState, keyof BLCmds>>
);

const isWeb =
  typeof window !== 'undefined' &&
  (!window.cordova || (window as any).Device?.platform === 'web');
let ZephyrDfu: any = null;
if (!isWeb) {
  ZephyrDfu = require('zephyr-dfu').ZephyrDfu;
}

// ----------------------------------------------------------------------------
// FSMService class
// ----------------------------------------------------------------------------

export class FSMService {
  /**
   * Speichert die gesamte Recording-Zeit (in ms) der aktuellen Session.
   */
  public totalRecordingTime: number = 0;
  /**
   * Startzeitpunkt der aktuellen Recording-Session (ms since epoch)
   */
  private recordingStartTime: number | null = null;
  dispatch(arg0: { type: string }) {
    throw new Error('Method not implemented.');
  }

  // --------------------------------------------------------------
  // Constants
  // --------------------------------------------------------------

  //
  private readonly FREQUENCY_BLE = 2500; // ms
  //
  private readonly TIMEOUT_RECORD = 5000; // ms
  //
  private readonly TIMEOUT_VIDEO_RECORDING = 2000; // ms
  //
  private readonly TIMEOUT_RECORDING = 45000; // ms
  //
  private readonly MIN_STATE_MIRROR_INTERVAL = 2000; // ms
  // Minimum number of samples to be collected to show the modal
  private readonly MIN_SAMPLES = 400;
  // Interval for showing the samples count info message
  private readonly INFO_SAMPLES_INTERVAL = 100; // samples
  // Timeout for ble connection
  private readonly TIMEOUT_CONNECT = 15000; // ms
  // Timeout is initial connection
  private readonly TIMEOUT_IS_INITIAL_CONNECTION = 1000; // ms
  // Standard pause
  private readonly PAUSE = 300; // ms
  //
  private readonly TIMEOUT_CONNECT_PROMISE = 2000; // ms

  // --------------------------------------------------------------
  // Private variables
  // --------------------------------------------------------------

  // BLE
  private _stateBeforeDisconnect: FSMState | null = null;
  private ble: BluetoothLEService;
  private lastKnown: Instance<typeof BluetoothInfoModel>;

  // Debug store
  private debugStore: DebugStore;

  // Subscriptions
  private subscriptions: Array<Lambda> = [];

  // Neue private Variablen
  private _isAutoResumeRecording: boolean = false;
  private _lastSampleTime: number = 0;
  private _stateCheckInterval: boolean = false;
  private _isCheckingState = false;

  // State mirroring
  private _lastStateCheckTime = 0;
  private _pendingStateCheck = false;

  // Private Variablen für die Zeitmessung
  private _recordingTimeoutId: number | null = null;

  // ----------------------------------------------------------------------------
  // Public variables
  // ----------------------------------------------------------------------------

  recordSessions: ST.RecordSample[][] = [[]];
  finalSamples: ST.RecordSample[] = [];

  /**
   * Enthält für jede Session ein Tupel [startTimestamp, endTimestamp].
   * endTimestamp ist null, solange die Session noch läuft.
   */
  recordSessionTimestamps: Array<[number, number | null]> = [
    [Date.now(), null],
  ];

  /**
   * Getter: Gibt die Session-Timestamps zurück, wobei laufende Sessions ein gültiges Enddatum (Date.now()) erhalten.
   */
  get recordSessionTimestampsResolved(): Array<[number, number]> {
    if (!Array.isArray(this.recordSessionTimestamps)) return [];
    // Defensive: Nur Tupel mit gültigen Zahlen zurückgeben, sonst leeres Array
    return this.recordSessionTimestamps
      .filter((arr) => Array.isArray(arr) && typeof arr[0] === 'number')
      .map(([start, end]) => [
        start,
        typeof end === 'number' ? end : Date.now(),
      ]);
  }

  // ----------------------------------------------------------------------------
  // Observable variables
  // ----------------------------------------------------------------------------

  batteryLevel: IObservableValue<number | undefined> = observable.box(
    undefined,
    {
      name: 'fsm.batteryLevel',
    }
  );

  _isInitialConnection: IObservableValue<boolean> = observable.box(false, {
    name: 'fsm.isInitialConnection',
  });

  _isFetchingState: IObservableValue<boolean> = observable.box(false, {
    name: 'fsm.isFetchingState',
  });

  deviceId: IObservableValue<string | null> = observable.box(null, {
    name: 'fsm.deviceId',
  });

  userInputBle: IObservableValue<StatesBLE> = observable.box(
    StatesBLE.DISCONNECTED,
    {
      name: 'fsm.userInputBleState',
    }
  );
  userInputState: IObservableValue<StatesUserInput> = observable.box(
    StatesUserInput.Idle,
    {
      name: 'fsm.userInputState',
    }
  );
  bleDevice: IObservableValue<BluetoothInfo | null> = observable.box(null, {
    name: 'fsm.bleDevice',
  });
  deviceState: IObservableValue<BLEDongleState | null>;
  canRecord: IObservableValue<boolean> = observable.box(false, {
    name: 'canRecord',
  });


  // ----------------------------------------------------------------------------
  // Firmware-Update: globaler Status & History für persistentes Modal
  // ----------------------------------------------------------------------------
  public firmwareUpdateStatus = observable.box<FirmwareUpdateStatus | null>(null, { name: 'fsm.firmwareUpdateStatus' });
  public firmwareUpdateError = observable.box<string | null>(null, { name: 'fsm.firmwareUpdateError' });
  public firmwareUpdateProgress = observable.box<number | null>(null, { name: 'fsm.firmwareUpdateProgress' });
  public firmwareUpdateTotal = observable.box<number | null>(null, { name: 'fsm.firmwareUpdateTotal' });
  public firmwareUpdateCancel = observable.box<(() => void) | null>(null, { name: 'fsm.firmwareUpdateCancel' });

  // Firmware-Update-History als Observable-Array
  public firmwareUpdateHistory = observable.array<FirmwareUpdateHistoryEntry>([], { name: 'fsm.firmwareUpdateHistory' });

  // History laden (z.B. beim App-Start)
  loadFirmwareUpdateHistory = action('loadFirmwareUpdateHistory', () => {
    try {
      const saved = localStorage.getItem('firmware-update-history');
      if (saved) {
        const parsed: FirmwareUpdateHistoryEntry[] = JSON.parse(saved);
        // Datumsfelder wiederherstellen
        this.firmwareUpdateHistory.replace(
          parsed.map((entry) => ({
            ...entry,
            startTime: entry.startTime ? new Date(entry.startTime) : undefined,
            endTime: entry.endTime ? new Date(entry.endTime) : undefined,
          }))
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load firmware update history:', e);
    }
  });

  // History speichern
  saveFirmwareUpdateHistory = action('saveFirmwareUpdateHistory', () => {
    try {
      localStorage.setItem('firmware-update-history', JSON.stringify(this.firmwareUpdateHistory.slice()));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to save firmware update history:', e);
    }
  });

  // Eintrag zur History hinzufügen
  addFirmwareUpdateHistory = action('addFirmwareUpdateHistory', (entry: FirmwareUpdateHistoryEntry) => {
    this.firmwareUpdateHistory.unshift(entry);
    // Maximal 100 Einträge behalten
    if (this.firmwareUpdateHistory.length > 100) {
      this.firmwareUpdateHistory.pop();
    }
    this.saveFirmwareUpdateHistory();
  });

  // History löschen
  clearFirmwareUpdateHistory = action('clearFirmwareUpdateHistory', () => {
    this.firmwareUpdateHistory.clear();
    this.saveFirmwareUpdateHistory();
  });

  resetFirmwareUpdateState = action('resetFirmwareUpdateState', () => {
    this.firmwareUpdateStatus.set(null);
    this.firmwareUpdateError.set(null);
    this.firmwareUpdateProgress.set(null);
    this.firmwareUpdateTotal.set(null);
    this.firmwareUpdateCancel.set(null);
    this._isUpdatingFirmware.set(false);
  });

  startFirmwareUpdate = action(
    'startFirmwareUpdate',
    async (
      fileName: string,
      updateType: 'production' | 'beta' | 'reset',
      getFirmwareData: () => Promise<{ b64: string; fileName: string }>
    ) => {
      this._isUpdatingFirmware.set(true);
      this.firmwareUpdateStatus.set({
        fileName,
        status: 'initializing',
        startTime: new Date(),
        message: `Preparing ${updateType} update...`,
      });
      this.firmwareUpdateError.set(null);
      this.firmwareUpdateProgress.set(null);
      this.firmwareUpdateTotal.set(null);
      this.firmwareUpdateCancel.set(null);

      let cancelRequested = false;
      const cancel = () => {
        cancelRequested = true;
        const prev = this.firmwareUpdateStatus.get();
        this.firmwareUpdateStatus.set({
          ...prev,
          fileName: prev?.fileName || '',
          status: 'failed',
          endTime: new Date(),
          message: 'Update abgebrochen',
        });
        this._isUpdatingFirmware.set(false);
      };
      this.firmwareUpdateCancel.set(cancel);

      try {
        const { b64, fileName: realFileName } = await getFirmwareData();
        if (cancelRequested) return false;
        this.firmwareUpdateStatus.set({
          ...this.firmwareUpdateStatus.get(),
          fileName: realFileName,
          status: 'uploading',
          message: 'Firmware wird installiert...',
        });
        const fwResponse = {
          type: 'updateavailable' as const,
          fwData: b64,
          fileName: realFileName,
        };
        const updateResult = await this.updateFirmware(
          fwResponse,
          (response: ZephyrResponse | Error) => {
            if (cancelRequested) return;
            if (response instanceof Error) {
              const prev = this.firmwareUpdateStatus.get();
              this.firmwareUpdateStatus.set({
                ...prev,
                fileName: prev?.fileName || '',
                status: 'failed',
                message: response.message,
                errorDetails: response.stack,
                endTime: new Date(),
              });
              this.firmwareUpdateError.set(response.message);
              this._isUpdatingFirmware.set(false);
            } else {
              let status: FirmwareUpdateStatus['status'] = 'uploading';
              if (response.status === 'upgradeCompleted') status = 'completed';
              if (
                response.status === 'upgradeFailed' ||
                response.status === 'upgradeCanceled'
              )
                status = 'failed';
              const prev = this.firmwareUpdateStatus.get();
              this.firmwareUpdateStatus.set({
                ...prev,
                fileName: prev?.fileName || '',
                status,
                message: response.status,
                endTime:
                  status === 'completed' || status === 'failed'
                    ? new Date()
                    : undefined,
              });
              if (status === 'completed' || status === 'failed') {
                this._isUpdatingFirmware.set(false);
              }
            }
          }
        );
        if (cancelRequested) return false;
        const prev = this.firmwareUpdateStatus.get();
        this.firmwareUpdateStatus.set({
          ...prev,
          fileName: prev?.fileName || '',
          status: updateResult ? 'completed' : 'failed',
          endTime: new Date(),
          message: updateResult
            ? 'Firmware update completed successfully'
            : 'Firmware update failed',
        });
        this._isUpdatingFirmware.set(false);
        return updateResult;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        const prev = this.firmwareUpdateStatus.get();
        this.firmwareUpdateStatus.set({
          ...prev,
          fileName: prev?.fileName || '',
          status: 'failed',
          message: errorMessage,
          errorDetails: error instanceof Error ? error.stack : undefined,
          endTime: new Date(),
        });
        this.firmwareUpdateError.set(errorMessage);
        this._isUpdatingFirmware.set(false);
        return false;
      }
    }
  );

  cancelFirmwareUpdate = action('cancelFirmwareUpdate', () => {
    const cancel = this.firmwareUpdateCancel.get();
    if (cancel) cancel();
  });

  setFirmwareUpdateProgress = action(
    'setFirmwareUpdateProgress',
    (progress: number, total: number) => {
      this.firmwareUpdateProgress.set(progress);
      this.firmwareUpdateTotal.set(total);
    }
  );
  private connectionLostCounter: IObservableValue<number> = observable.box(0, {
    name: 'fsm.connectionLostCounter',
  });
  connectionLost = new Signal<void>();

  private setBleDevice(device: BluetoothInfo | null) {
    var firmwareVersion = 'unknown';
    var osVersion = 'unknown';
    const state = this.deviceState.get();
    if (state) {
      firmwareVersion = state.versionFW;
      osVersion = state.versionOS;
    }
    Sentry.setTags({
      'freak.firmwareVersion': firmwareVersion,
      'freak.osVersion': osVersion,
    });
    runInAction(() => this.bleDevice.set(device));
  }

  // Observable for sensor state
  public fsmSensorMirror: IObservableValue<FSMState | null> =
    observable.box(null);

  private _lastStateLog: { timestamp: number; state: FSMState } | null = null;

  // ----------------------------------------------------------------------------
  // Constructor and destructor
  // ----------------------------------------------------------------------------

  constructor(
    bleService: BluetoothLEService,
    lastKnown: Instance<typeof BluetoothInfoModel>,
    debugStore: DebugStore
  ) {
    makeAutoObservable(this);
    this.ble = bleService;
    this.deviceState = this.ble.deviceState;

    this.lastKnown = lastKnown;
    this.setBleDevice(this.lastKnown);

    this.debugStore = debugStore;

    // ----------------------------------------------------------------------------
    // Subscription setup
    // ----------------------------------------------------------------------------

    const recordSub = this.ble.recordObserver.subscribe(
      this._collectRecordSample
    );
    this.subscriptions.push(() => recordSub.unsubscribe());
    // const dataSub = this.ble.dataObserver.subscribe(this._onData);
    // this.subscriptions.push(() => dataSub.unsubscribe());
    this.subscriptions.push(
      observe(
        this.ble.bleDevice,
        (changed: IValueDidChange<BluetoothInfo | null>) => {
          const info = toJS(changed.newValue as BluetoothInfo);
          this.lastKnown.setFrom(info);
          this.setBleDevice(info);
        }
      )
    );

    // Starte handleBle einmal pro Sekunde
    setInterval(() => {
      this.handleBle().catch((error) => {
        logger.error(
          'fsm - constructor - Error in periodic bluetooth handler:',
          error
        );
      });
    }, this.FREQUENCY_BLE);

    if (this.lastKnown.entry?.id) {
      this.ble.checkConnected(this.lastKnown.entry?.id);
    }
  }

  /**
   * Aktualisiert die connectionTimeline anhand der recordSessionTimestamps.
   * Jede Session wird als 'lostButReceived' (gelb) visualisiert.
   * Die Zeit zwischen Sessions (falls Lücke > 1s) wird als 'disconnected' (rot) visualisiert.
   * Die Zeit der ersten Session beginnt mit dem ersten Timestamp.
   */
  public updateConnectionTimelineFromSessions(): void {
    this.connectionTimeline = [];
    const sessions = this.recordSessionTimestamps;
    if (!Array.isArray(sessions) || sessions.length === 0) {
      this.bleConnected = 0;
      this.bleDisconnected = 0;
      this.totalTime = 0;
      this.totalRecordingTime = 0;
      return;
    }
    // Sortiere Sessions nach Startzeit
    const sorted = sessions
      .filter(
        (s): s is [number, number | null] =>
          typeof s[0] === 'number' &&
          (typeof s[1] === 'number' || s[1] === null)
      )
      .sort((a, b) => a[0] - b[0]);
    let lastEnd: number | null = null;
    for (let i = 0; i < sorted.length; i++) {
      const [start, end] = sorted[i];
      if (lastEnd !== null && start - lastEnd > 1000) {
        this.connectionTimeline.push({
          type: 'disconnected',
          start: lastEnd,
          end: start,
        });
      }
      // Session: connected (grün)
      this.connectionTimeline.push({
        type: 'connected',
        start,
        end: end ?? Date.now(),
      });
      lastEnd = end ?? Date.now();
    }
    // Summen live berechnen
    this.bleConnected = this.connectionTimeline
      .filter((e) => e.type === 'connected')
      .reduce((acc, e) => acc + (e.end - e.start), 0);
    this.bleDisconnected = this.connectionTimeline
      .filter((e) => e.type === 'disconnected')
      .reduce((acc, e) => acc + (e.end - e.start), 0);
    this.totalTime = this.connectionTimeline.reduce(
      (acc, e) => acc + (e.end - e.start),
      0
    );
    this.totalRecordingTime = this.recordSessionTimestamps.reduce(
      (acc, [start, end]) => {
        if (
          typeof start === 'number' &&
          typeof end === 'number' &&
          end > start
        ) {
          return acc + (end - start);
        }
        return acc;
      },
      0
    );
  }

  destroy() {
    this.subscriptions.forEach((sub) => sub());
    this.subscriptions.length = 0;
  }

  // ----------------------------------------------------------------------------
  // State mirroring and comparison
  // ----------------------------------------------------------------------------

  private async fetchSensorState(): Promise<boolean> {
    // Prüfen, ob eine Abfrage läuft oder zu kurze Zeit seit der letzten vergangen ist
    if (this._isFetchingState.get()) {
      logger.debug('fsm - fetchSensorState - Already fetching state, skipping');
      return false;
    }

    const now = Date.now();
    const timeSinceLastCheck = now - this._lastStateCheckTime;
    if (timeSinceLastCheck < this.MIN_STATE_MIRROR_INTERVAL) {
      logger.debug(
        `fsm - fetchSensorState - Last check was ${timeSinceLastCheck}ms ago, minimum is ${this.MIN_STATE_MIRROR_INTERVAL}ms`
      );
      return false;
    }

    // Status auf "fetching" setzen
    runInAction(() => {
      this.setIsFetchingState(true);
    });
    this._lastStateCheckTime = now;

    try {
      logger.info('fsm - fetchSensorState - Asking sensor for state...');

      // Befehl senden und auf Antwort warten
      const response = await this.ble.sendCmd(new Pck.PckCmdInfo());

      // Status zurücksetzen
      runInAction(() => {
        this.setIsFetchingState(false);
      });

      // Antwort verarbeiten
      if (!response) {
        logger.warn('fsm - fetchSensorState - Received null response');
        return false;
      }

      // Zustand aktualisieren
      return runInAction(() => {
        try {
          this.updateSensorState(
            response as InstanceType<typeof Pck.PckRspInfo>
          );
          logger.debug(
            'fsm - fetchSensorState - Sensor state update successful'
          );
          this.compare();
          return true;
        } catch (error) {
          logger.error(
            'fsm - fetchSensorState - Failed to update sensor state:',
            error
          );
          return false;
        }
      });
    } catch (error) {
      // Status zurücksetzen
      runInAction(() => {
        this.setIsFetchingState(false);
      });

      if (
        error instanceof Error &&
        error.message.includes('already a command')
      ) {
        logger.debug(
          'fsm - fetchSensorState - Command already in progress (should not happen)'
        );
      } else {
        logger.warn(
          'fsm - fetchSensorState - Failed to fetch sensor state:',
          error
        );
      }

      return false;
    }
  }

  private updateSensorState(info: InstanceType<typeof Pck.PckRspInfo>) {
    if (!info) {
      logger.warn('fsm - updateSensorState - Received null info object');
      return;
    }

    runInAction(() => {
      const versionString = String.fromCharCode(
        ...info.spinnax_freak_version_string.filter((c) => c !== 0)
      );

      // Map the numeric state to FSMState enum
      const mappedState = this.mapDongleStateToFSMState(
        info.dongle_state.fsm_state
      );

      // Scale the battery level from 30-100 to 0-100
      let scaledBatteryLevel = info.dongle_state.battery_level;
      if (scaledBatteryLevel !== undefined) {
        scaledBatteryLevel = Math.round(
          ((scaledBatteryLevel - 50) / (100 - 50)) * 100
        );
        // Ensure the value is within the 0-100 range
        scaledBatteryLevel = Math.max(0, Math.min(100, scaledBatteryLevel));
      }

      // Updates in Action gruppieren
      runInAction(() => {
        this.setBatteryLevel(scaledBatteryLevel);
        this.fsmSensorMirror.set(mappedState);
      });

      // Optionales Logging
      logger.info(
        'fsm - updateSensorState - fsm state:',
        info.dongle_state.fsm_state
      );
    });
  }

  // Ersetze die alte stopStateCheck Methode
  private stopPolling() {
    if (this._stateCheckInterval) {
      logger.info('fsm - stopPolling - Stopping state polling');
      this._stateCheckInterval = false;
      this._pendingStateCheck = false;
    }
  }

  public handleBleLoss() {
    logger.warn(
      'fsm - handleBleLoss - Connection lost, stopping state polling'
    );
    // Session-Ende setzen, falls noch offen
    const sessionIdx = this.connectionLostCounter.get();
    if (
      this.recordSessionTimestamps[sessionIdx] &&
      this.recordSessionTimestamps[sessionIdx][1] === null
    ) {
      this.recordSessionTimestamps[sessionIdx][1] = Date.now();
      console.log(
        '[FSMService] handleBleLoss: Session-Ende gesetzt:',
        this.recordSessionTimestamps[sessionIdx]
      );
    } else {
      console.warn(
        '[FSMService] handleBleLoss: Session-Ende NICHT gesetzt! sessionIdx:',
        sessionIdx,
        'recordSessionTimestamps:',
        this.recordSessionTimestamps
      );
    }
    // Debug: Array-Länge nach Disconnect
    console.log(
      '[FSMService] handleBleLoss: recordSessionTimestamps nach Disconnect:',
      this.recordSessionTimestamps
    );
    runInAction(() => {
      this.connectionLostCounter.set(this.connectionLostCounter.get() + 1);
    });
    logger.info(
      'fsm - handleBleLoss - Connection lost counter:',
      this.connectionLostCounter.get()
    );
    this.stopPolling();
  }

  public async handleBle() {
    // logger.info('fsm - handleBle - Connection state:', this.bleState);
    // logger.info(
    //   'fsm - handleBle - User wants BLE state:',
    //   this.userInputBle.get()
    // );
    switch (this.bleState) {
      case StatesBLE.CONNECTED:
        if (this.userInputBle.get() === StatesBLE.CONNECTED) {
          logger.info('fsm - handleBle - Connected to Bluetooth device');
          runInAction(() => {
            this.canRecord.set(true);
          });
          await this.compare();
        } else if (this.userInputBle.get() === StatesBLE.CONNECTING) {
          logger.info(
            'fsm - handleBle - User wants to disconnect from Bluetooth device'
          );
          this.setUserInputBle(StatesBLE.CONNECTED);
        } else {
          logger.warn(
            'fsm - handleBle - User wants to disconnect from Bluetooth device'
          );
          runInAction(() => {
            this.canRecord.set(false);
            this.fsmSensorMirror.set(null);
            this.setBatteryLevel(undefined);
          });
          try {
            await this.ble.disconnect();
          } catch (disconnectError) {
            logger.error(
              'fsm - handleBle - Disconnect failed:',
              disconnectError
            );
          }
        }
        break;
      case StatesBLE.DISCONNECTED:
        if (this.userInputBle.get() === StatesBLE.DISCONNECTED) {
          // logger.info('fsm - handleBle - Disconnected from Bluetooth device');
          runInAction(() => {
            this.userInputState.set(StatesUserInput.Idle);
          });
        } else if (this.userInputBle.get() === StatesBLE.CONNECTING) {
          logger.info(
            'fsm - handleBle - User wants to connect to Bluetooth device'
          );
          runInAction(() => {
            this.setCanRecord(false);
            this.fsmSensorMirror.set(null); // Reset sensor mirror state
            this.setBatteryLevel(undefined);
          });
          try {
            await this.connectToSensor();
          } catch (connectError) {
            logger.error('fsm - handleBle - Connect failed:', connectError);
            // Reset state on connection failure
            this.setUserInputBle(StatesBLE.DISCONNECTED);
          }
        } else {
          runInAction(() => {
            this.setIsFetchingState(false);
            this._pendingStateCheck = false;
            this.setCanRecord(false);
            this.fsmSensorMirror.set(null); // Reset sensor mirror state
            this.setBatteryLevel(undefined);
          });
          this.handleBleLoss();
          logger.warn('fsm - handleBle - User wants connection.');
          // if (this.lastKnown.entry?.id) {
          //   this.ble.autoConnect(this.lastKnown.entry.id);
          // } else {
          //   logger.warn(
          //     'fsm - handleBle - Cannot auto-connect: device ID is null'
          //   );
          // }
          this.connectToSensor();
        }
        break;
      case StatesBLE.CONNECTING:
        runInAction(() => {
          this.setCanRecord(false);
        });
        if (this.userInputBle.get() === StatesBLE.CONNECTED) {
          logger.info('fsm - constructor - Connecting to Bluetooth device');
          runInAction(() => {
            this.setCanRecord(false);
            this.fsmSensorMirror.set(null); // Reset sensor mirror state
            this.setBatteryLevel(undefined);
          });
          this.connectToSensor();
        } else {
          logger.warn(
            'fsm - constructor - User wants to disconnect from Bluetooth device'
          );
        }
        break;
      case StatesBLE.ERROR:
        logger.error('fsm - constructor - Error in connection state');
        runInAction(() => {
          this.canRecord.set(false);
        });
        break;
      default:
        break;
    }
  }

  compare = action('compare', async () => {
    try {
      await this.fetchSensorState();
    } catch (error) {
      logger.error('fsm - compare - Failed to fetch sensor state:', error);
      return;
    }
    logger.info('fsm - compare - State sensor: ', this.fsmSensorMirror.get());
    logger.info(
      'fsm - compare - State user input: ',
      this.userInputState.get()
    );

    const currentState = this.fsmSensorMirror.get();

    if (currentState === null) {
      logger.warn('fsm - compare - State is null, skipping comparison');
      runInAction(() => {
        const deviceId = this.bleDevice.get()?.entry?.id;
        if (deviceId) {
          this.setDeviceId(deviceId);
        }
      });
      try {
        await this.disconnectFromSensor();
      } catch (error) {
        logger.error(
          'fsm - compare - Failed to disconnect from sensor:',
          error
        );
      }
      return;
    }

    if (this.userInputState === null) {
      logger.warn(
        'fsm - compare - User input state is null, skipping comparison'
      );
      return;
    }

    switch (currentState) {
      case FSMState.Idle:
        if (this.userInputState.get() === StatesUserInput.Idle) {
          // this.recordCallbacks.resetTimer?.();
          // this.recordCallbacks.stopTimer?.();
        } else if (this.userInputState.get() === StatesUserInput.Record) {
          logger.warn(
            'fsm - compare - State is IDLE, but user input is RECORDING'
          );
          try {
            await this.ble.sendCmd(new Pck.PckCmdReBeg());
          } catch (error) {
            logger.error('fsm - compare - Failed to send RECORD command');
            return;
          }
          await withTimeout(
            this.TIMEOUT_VIDEO_RECORDING,
            t('RECORDING_VIDEO_TIMEOUT'),
            () => this.startRecording()
          ).catch((error) => {
            logger.error('Failed to start recording:', error);
            this.stopRecording().catch((error) => {
              logger.error('fsm - compare - Failed to stop recording:', error);
            });
            return;
          });
        } else if (this.userInputState.get() === StatesUserInput.Tare) {
          logger.debug('fsm - compare - User wants Tare/Maintain');
          try {
            await this.ble.sendCmd(new Pck.PckCmdMaBeg());
          } catch (error) {
            logger.error('fsm - compare - Failed to send MAINTAIN command');
            return;
          }
          // await new Promise((resolve) => setTimeout(resolve, 2000));
          // this.ble.sendCmd(new Pck.PckCmdTare());
          // await new Promise((resolve) => setTimeout(resolve, 2000));
          // runInAction(() => {
          //   this.userInputState.set(StatesUserInput.Idle);
          // });
        }
        break;
      case FSMState.Record:
        if (this.userInputState.get() === StatesUserInput.Idle) {
          logger.debug(
            'fsm - compare - State is RECORD, but user input is IDLE'
          );
          try {
            await this.ble.sendCmd(new Pck.PckCmdIdBeg());
            await this.stopRecording();
          } catch (error) {
            logger.error('fsm - compare - Failed to stop recording:', error);
          }
        } else if (this.userInputState.get() === StatesUserInput.Record) {
          logger.info('fsm - compare - State is RECORD, user input is RECORD');
          // Start recording
        }
        break;
      case FSMState.Busy:
        if (this.userInputState.get() === StatesUserInput.Idle) {
          logger.debug('fsm - compare - State is BUSY, but user input is IDLE');
          try {
            await this.ble.sendCmd(new Pck.PckCmdIdBeg());
          } catch (error) {
            logger.error('fsm - compare - Failed to send IDLE command:', error);
          }
        }
        break;
      case FSMState.Play:
        if (this.userInputState.get() === StatesUserInput.Idle) {
          logger.debug('fsm - compare - State is PLAY, but user input is IDLE');
          try {
            await this.ble.sendCmd(new Pck.PckCmdIdBeg());
          } catch (error) {
            logger.error('fsm - compare - Failed to send IDLE command:', error);
          }
        }
        break;
      case FSMState.Maintain:
        if (this.userInputState.get() === StatesUserInput.Idle) {
          logger.debug(
            'fsm - compare - State is MAINTAIN, but user input is IDLE'
          );
          try {
            await this.ble.sendCmd(new Pck.PckCmdIdBeg());
          } catch (error) {
            logger.error('fsm - compare - Failed to send IDLE command:', error);
          }
        } else if (this.userInputState.get() === StatesUserInput.Tare) {
          logger.debug('fsm - compare - State is MAINTAIN, for Taring');
          this.tare();
          runInAction(() => {
            this.userInputState.set(StatesUserInput.Idle);
          });
        } else {
          logger.warn(
            'fsm - compare - State is MAINTAIN, but user input is not IDLE'
          );
          try {
            await this.ble.sendCmd(new Pck.PckCmdIdBeg());
          } catch (error) {
            logger.error('fsm - compare - Failed to send IDLE command:', error);
          }
        }
        break;
      default:
        logger.debug(
          `fsm - compare - Unknown state: ${currentState}. Cannot compare with user input.`
        );
        break;
    }
  });

  // -----------------------------------------------------------------
  // Tare
  // -----------------------------------------------------------------
  async tare(): Promise<void> {
    try {
      await this.ble.sendCmd(new Pck.PckCmdTare());
    } catch (error) {
      logger.error('fsm - tare - Tare operation failed:', error);
    }
  }

  // ------------------------------------------------------------------
  // Sample collection
  // ------------------------------------------------------------------

  /**
   * Timeline-Events für die Visualisierung im Modal.
   * Jedes Event: { type: 'connected' | 'disconnected' | 'lostButReceived', start: number, end: number }
   */
  public connectionTimeline: Array<{
    type: 'connected' | 'disconnected' | 'lostButReceived';
    start: number;
    end: number;
  }> = [];
  /**
   * Aufsummierte Zeit in ms, in der BLE verbunden war (für Modal)
   */
  public bleConnected: number = 0;
  /**
   * Aufsummierte Zeit in ms, in der BLE getrennt war (für Modal)
   */
  public bleDisconnected: number = 0;
  /**
   * Gesamtdauer der Recording-Session (für Modal)
   */
  public totalTime: number = 0;
  /**
   * Letzter bekannter BLE-Status ("connected" | "disconnected" | "lostButReceived")
   */
  private lastBleState:
    | 'connected'
    | 'disconnected'
    | 'lostButReceived'
    | null = null;
  /**
   * Zeitstempel, wann der aktuelle BLE-Status begonnen hat
   */
  private lastBleStateStart: number | null = null;

  /**
   * Getter: Aufsummierte Zeit in ms, in der BLE verbunden war (inkl. laufendem Event)
   */
  get bleConnectedLive(): number | undefined {
    if (!this.connectionTimeline || this.connectionTimeline.length === 0)
      return undefined;
    const now = Date.now();
    let sum = this.connectionTimeline
      .filter((e) => e.type === 'connected')
      .reduce((acc, e) => acc + (e.end - e.start), 0);
    if (this.lastBleState === 'connected' && this.lastBleStateStart !== null) {
      sum += now - this.lastBleStateStart;
    }
    return isNaN(sum) ? undefined : sum;
  }
  /**
   * Getter: Aufsummierte Zeit in ms, in der BLE getrennt war (inkl. laufendem Event)
   */
  get bleDisconnectedLive(): number | undefined {
    if (!this.connectionTimeline || this.connectionTimeline.length === 0)
      return undefined;
    const now = Date.now();
    let sum = this.connectionTimeline
      .filter((e) => e.type === 'disconnected')
      .reduce((acc, e) => acc + (e.end - e.start), 0);
    if (
      this.lastBleState === 'disconnected' &&
      this.lastBleStateStart !== null
    ) {
      sum += now - this.lastBleStateStart;
    }
    return isNaN(sum) ? undefined : sum;
  }
  /**
   * Getter: Gesamtdauer der Recording-Session (inkl. laufendem Event)
   */
  get totalTimeLive(): number | undefined {
    if (!this.connectionTimeline || this.connectionTimeline.length === 0)
      return undefined;
    const now = Date.now();
    let sum = this.connectionTimeline.reduce(
      (acc, e) => acc + (e.end - e.start),
      0
    );
    if (this.lastBleState && this.lastBleStateStart !== null) {
      sum += now - this.lastBleStateStart;
    }
    return isNaN(sum) ? undefined : sum;
  }

  /**
   * Getter: Gibt BLE-Timeline-Intervalle für die Visualisierung zurück.
   * Format: Array<[start: number, end: number, connected: true | false | 'yellow']>
   * Grün: BLE verbunden, Gelb: getrennt aber Samples empfangen, Rot: getrennt und keine Samples
   */
  get bleTimesteps(): Array<[number, number, true | false | 'yellow']> {
    // Nur abgeschlossene Sessions (end != null)
    const sessions = Array.isArray(this.recordSessionTimestamps)
      ? this.recordSessionTimestamps.filter(
          ([s, e]) => typeof s === 'number' && typeof e === 'number' && e > s
        )
      : [];
    if (sessions.length === 0) return [];
    // Timeline: von minStart bis maxEnd (nur number)
    const sessionStarts = sessions
      .map(([s]) => (typeof s === 'number' ? s : null))
      .filter((s): s is number => typeof s === 'number');
    const sessionEnds = sessions
      .map(([, e]) => (typeof e === 'number' ? e : null))
      .filter((e): e is number => typeof e === 'number');
    const min = sessionStarts.length > 0 ? Math.min(...sessionStarts) : 0;
    const max = sessionEnds.length > 0 ? Math.max(...sessionEnds) : 0;
    const sampleTimesTimeline = Array.isArray(this.recordSamples)
      ? this.recordSamples
          .map((s) => (typeof s.t_ms === 'number' ? s.t_ms : null))
          .filter((t): t is number => typeof t === 'number')
      : [];
    let intervals: {
      start: number;
      end: number;
      connected: true | false | 'yellow';
    }[] = [];
    // 1. Verbundene Intervalle (grün)
    sessions.forEach(([start, end]) => {
      if (typeof start === 'number' && typeof end === 'number') {
        intervals.push({ start, end, connected: true });
      }
    });
    // 2. Getrennte Intervalle (zwischen Sessions)
    // Nur anzeigen, wenn Nutzer BLE wollte (userInputBle == CONNECTED)
    const userWantsBle = this.userInputBle?.get?.() === 'CONNECTED';
    let onlyGreen = true;
    for (let i = 1; i < sessions.length; i++) {
      const prevEnd = sessions[i - 1][1];
      const currStart = sessions[i][0];
      if (
        typeof prevEnd === 'number' &&
        typeof currStart === 'number' &&
        currStart > prevEnd &&
        userWantsBle
      ) {
        // Prüfe, ob Samples in diesem Intervall
        const hasSamples = sampleTimesTimeline.some(
          (t) => typeof t === 'number' && t > prevEnd && t < currStart
        );
        if (!hasSamples) onlyGreen = false;
        intervals.push({
          start: prevEnd,
          end: currStart,
          connected: hasSamples ? 'yellow' : false,
        });
      }
    }
    // 3. Vor dem ersten und nach dem letzten Session-Intervall NUR wenn mehrere Sessions existieren
    if (sessions.length > 1) {
      if (min > 0 && userWantsBle) {
        // Vor dem ersten
        const hasSamples = sampleTimesTimeline.some(
          (t) => typeof t === 'number' && t < min
        );
        if (!hasSamples && min > 0) onlyGreen = false;
        if (hasSamples || min > 0)
          intervals.push({
            start: 0,
            end: min,
            connected: hasSamples ? 'yellow' : false,
          });
      }
      if (max > 0 && userWantsBle) {
        // Nach dem letzten
        const hasSamples = sampleTimesTimeline.some(
          (t) => typeof t === 'number' && t > max
        );
        if (hasSamples)
          intervals.push({ start: max, end: max + 1, connected: 'yellow' });
      }
    }
    // Wenn alle Verbindungsintervalle direkt aneinander liegen (keine echten Lücken) und keine gelben/roten Intervals, alles grün
    if (onlyGreen && intervals.every((i) => i.connected === true)) {
      return intervals.map(({ start, end }) => [start, end, true]);
    }
    // Sortiere nach Start (nur number)
    intervals = intervals.sort((a, b) =>
      typeof a.start === 'number' && typeof b.start === 'number'
        ? a.start - b.start
        : 0
    );
    // In bleTimesteps-Format bringen: [start, end, connected]
    return intervals.map(({ start, end, connected }) => [
      start,
      end,
      connected,
    ]);
  }

  _collectRecordSample = {
    next: (sample: COM.RecordSample) => {
      // Prüfe auf t_ms-Rollover (neue Session, z.B. nach Disconnect)
      let sessionIdx = this.connectionLostCounter.get();
      let currentSession = this.recordSessions[sessionIdx];
      const prevSample =
        currentSession && currentSession.length > 0
          ? currentSession[currentSession.length - 1]
          : undefined;
      // Wenn nach einem Disconnect das erste Sample kommt, muss eine neue Session anlegt werden
      if (
        !this.recordSessionTimestamps[sessionIdx] ||
        (this.recordSessionTimestamps[sessionIdx][1] !== null &&
          currentSession.length === 0)
      ) {
        // Neue Session anlegen
        const now = Date.now();
        this.recordSessions[sessionIdx] = [];
        this.recordSessionTimestamps[sessionIdx] = [now, null];
        currentSession = this.recordSessions[sessionIdx];
        console.log(
          '[FSMService] Neue Session nach Disconnect angelegt:',
          sessionIdx,
          this.recordSessionTimestamps
        );
      }
      if (
        prevSample &&
        typeof prevSample.t_ms === 'number' &&
        sample.t_ms < prevSample.t_ms
      ) {
        // BLE-Abbruch erkannt: t_ms ist nicht mehr monoton steigend
        console.warn(
          '[FSMService] BLE-Abbruch erkannt: t_ms-Rollover! Vorheriger t_ms:',
          prevSample.t_ms,
          'neuer t_ms:',
          sample.t_ms
        );
        // Sessionwechsel: Session abschließen und neue Session starten
        const now = Date.now();
        // Ende der alten Session setzen (nur, wenn noch offen)
        if (
          this.recordSessionTimestamps[sessionIdx] &&
          this.recordSessionTimestamps[sessionIdx][1] === null
        ) {
          this.recordSessionTimestamps[sessionIdx][1] = now;
        }
        // Counter erhöhen und neue Session anlegen
        this.connectionLostCounter.set(this.connectionLostCounter.get() + 1);
        sessionIdx = this.connectionLostCounter.get();
        this.recordSessions[sessionIdx] = [];
        // Start der neuen Session exakt auf das Ende der alten Session setzen
        this.recordSessionTimestamps[sessionIdx] = [now, null];
        currentSession = this.recordSessions[sessionIdx];
        // Timeline nach Sessionwechsel aktualisieren
        this.updateConnectionTimelineFromSessions();
        // Debug: Session-Timestamps nach Sessionwechsel ausgeben
        const abs = this.recordSessionTimestamps.map(([s, e]) => [s, e]);
        const base = typeof abs[0]?.[0] === 'number' ? abs[0][0] : 0;
        const rel = abs.map(([s, e]) => [
          typeof s === 'number' ? ((s - base) / 1000).toFixed(2) : null,
          typeof e === 'number' ? ((e - base) / 1000).toFixed(2) : null,
        ]);
        console.log(
          '[FSMService] Sessionwechsel: recordSessionTimestamps (abs):',
          abs,
          'rel:',
          rel
        );
      }
      // Zeitstempel für Session-Start setzen, falls noch nicht gesetzt
      if (!this.recordSessionTimestamps[sessionIdx]) {
        // Falls aus irgendeinem Grund keine Session existiert, mit aktuellem Zeitpunkt starten
        const now = Date.now();
        this.recordSessionTimestamps[sessionIdx] = [now, null];
      }
      // Timeline-Event für "lostButReceived" (gelb):
      // Timeline immer aktuell halten
      this.updateConnectionTimelineFromSessions();
      // Sample speichern
      this.recordSessions[sessionIdx].push(ST.RecordSample.of_bin(sample));

      // Logging nur noch bei Sessionwechsel oder neuer Session nach Disconnect (siehe oben)

      const totalSamples = this.recordSamples.length;
      if (totalSamples % this.INFO_SAMPLES_INTERVAL === 0) {
        logger.info(
          'fsm - _collectRecordSample - Record samples:',
          totalSamples
        );
        logger.info(
          'fsm - _collectRecordSample - Record sessions:',
          this.connectionLostCounter.get()
        );
        // Zusätzliches Logging für jedes 10. Sample
        if (totalSamples % 10 === 0) {
          // eslint-disable-next-line no-console
          const currentSample =
            this.recordSamples[this.recordSamples.length - 1];
          // MobX Proxies in echtes Array umwandeln
          const timestamps = Array.isArray(this.recordSessionTimestamps)
            ? this.recordSessionTimestamps.map((arr) =>
                Array.isArray(arr) ? [...arr] : arr
              )
            : this.recordSessionTimestamps;
          const now = Date.now();
          console.log(
            '[FSMService] SampleObj:',
            currentSample,
            'recordSessionTimestamps:',
            timestamps,
            'logTime:',
            now,
            new Date(now).toISOString()
          );
        }
      }
    },
    complete: () => {
      // Debug: Prüfen, ob complete() aufgerufen wird
      console.log('[FSMService] _collectRecordSample.complete() called');
      // Session-Ende-Zeit setzen
      const sessionIdx = this.connectionLostCounter.get();
      if (
        this.recordSessionTimestamps[sessionIdx] &&
        this.recordSessionTimestamps[sessionIdx][1] === null
      ) {
        this.recordSessionTimestamps[sessionIdx][1] = Date.now();
        console.log(
          '[FSMService] Session-Ende-Zeit gesetzt:',
          this.recordSessionTimestamps[sessionIdx]
        );
      }
    },
    error: (err: any) =>
      logger.error('fsm - _collectRecordSample - RecordSampleObserver: ' + err),
  };

  handleLog(data: Pck.PckRspLog) {
    const severity = _LogLevel[data.level ?? 25];
    const prefix = `Dongle [${severity}]`;
    const msg = numberArrayToString(data.text);

    Sentry.captureMessage(
      `${prefix} – ${msg}`,
      _.lowerCase(severity) as Sentry.SeverityLevel
    );
    // logger.log(prefix, msg);
  }

  async startRecording(): Promise<void> {
    logger.info('fsm - startRecording - User wants to start recording');
    // Recording-Startzeit setzen
    this.recordingStartTime = Date.now();
    // Timeline zurücksetzen
    this.connectionTimeline = [];
    this.lastBleState = null;
    this.lastBleStateStart = null;
    // Werte zurücksetzen
    this.bleConnected = 0;
    this.bleDisconnected = 0;
    this.totalTime = 0;

    return new Promise<void>((resolve, reject) => {
      let isResolved = false;
      let subscription: any = null;

      // ✅ FIXED: Safe resolve/reject helpers
      const safeResolve = () => {
        if (!isResolved) {
          isResolved = true;
          if (subscription) {
            subscription.unsubscribe();
          }
          resolve();
        }
      };

      const safeReject = (error: Error) => {
        if (!isResolved) {
          isResolved = true;
          if (subscription) {
            subscription.unsubscribe();
          }
          reject(error);
        }
      };

      try {
        // ✅ FIXED: Safe observer subscription
        subscription = this.ble.recordObserver.pipe(first()).subscribe({
          next: () => {
            logger.info('fsm - startRecording - First sample received');
            safeResolve();
          },
          error: (observerError) => {
            logger.error(
              'fsm - startRecording - Observer error:',
              observerError
            );
            safeReject(new Error('Bluetooth observer error'));
          },
        });

        // ✅ FIXED: Safe timeout with error handling
        setTimeout(() => {
          try {
            if (this.recordSamples.length > 0) {
              logger.info(
                'fsm - startRecording - Samples received within timeout'
              );
              safeResolve();
            } else {
              logger.warn(
                'fsm - startRecording - No samples received after timeout'
              );
              safeReject(new Error('Timeout while starting recording'));
            }
          } catch (timeoutError) {
            logger.error(
              'fsm - startRecording - Error in timeout handler:',
              timeoutError
            );
            safeReject(new Error('Timeout handler error'));
          }
        }, this.TIMEOUT_RECORD);
      } catch (error) {
        logger.error('fsm - startRecording - Setup error:', error);
        safeReject(error instanceof Error ? error : new Error('Setup error'));
      }
    });
  }

  async stopRecording(): Promise<void> {
    logger.info('fsm - stopRecording - User wants to stop recording');

    runInAction(() => {
      this._stateBeforeDisconnect = null;
      // Recording-Endzeit und Dauer berechnen
      const now = Date.now();
      if (this.recordingStartTime) {
        this.totalRecordingTime = now - this.recordingStartTime;
      } else {
        this.totalRecordingTime = 0;
      }
      this.recordingStartTime = null;

      // Offene Session abschließen
      const lastIdx = this.recordSessionTimestamps.length - 1;
      if (
        lastIdx >= 0 &&
        this.recordSessionTimestamps[lastIdx] &&
        this.recordSessionTimestamps[lastIdx][1] === null
      ) {
        this.recordSessionTimestamps[lastIdx][1] = now;
        logger.info(
          'fsm - stopRecording - Session abgeschlossen:',
          this.recordSessionTimestamps[lastIdx]
        );
      }
    });
    // Timeline nach Recording-Ende aktualisieren
    this.updateConnectionTimelineFromSessions();
    // Summen berechnen
    this.bleConnected = this.connectionTimeline
      .filter((e) => e.type === 'connected')
      .reduce((acc, e) => acc + (e.end - e.start), 0);
    this.bleDisconnected = this.connectionTimeline
      .filter((e) => e.type === 'disconnected')
      .reduce((acc, e) => acc + (e.end - e.start), 0);
    this.totalTime = this.connectionTimeline.reduce(
      (acc, e) => acc + (e.end - e.start),
      0
    );

    // Array-Längen validieren
    const flattenedLength = this.recordSessions.flat().length;
    const sessionLength = this.recordSessions[0].length;

    if (flattenedLength !== sessionLength) {
      logger.warn(
        'fsm - stopRecording - Inkonsistente Array-Längen:',
        `Flattened: ${flattenedLength}, Session: ${sessionLength}`
      );
    }

    logger.info('fsm - stopRecording - Sorted record samples by timestamp');
    logger.info('fsm - stopRecording - Record sessions:', this.recordSessions);
    logger.info(
      'fsm - stopRecording - Record samples length',
      this.recordSamples.length
    );
    logger.info('fsm - stopRecording - Record samples:', this.recordSamples);

    logger.info('fsm - stopRecording - Stopping recording and resetting state');
    logger.info('fsm - stopRecording - Showing modal');
  }

  // Hilfsmethode zum Abbrechen des Timers
  private clearRecordingTimeout() {
    if (this._recordingTimeoutId !== null) {
      clearTimeout(this._recordingTimeoutId);
      this._recordingTimeoutId = null;
      logger.debug('fsm - clearRecordingTimeout - Recording timeout cleared');
    }
  }

  // ----------------------------------------------------------------------------
  // Tare handling
  // ----------------------------------------------------------------------------

  // // In FSMService.ts - verbessere die tare-Methode
  // async tare(): Promise<void> {
  //   logger.info('fsm - tare - Taring the sensor...');
  //   try {
  //     await this.ble.sendCmd(new Pck.PckCmdTare());
  //   } catch (error) {
  //     logger.error('fsm - tare - Tare operation failed:', error);
  //   }
  // }

  // ----------------------------------------------------------------------------
  // Firmware update
  // ----------------------------------------------------------------------------

  // Check if we have a newer version on the server.
  // We need to send our current version to the server, which
  // will return the latest binary if there is a newer version available
  // or an empty response if we are up to date
  async checkForFirmwareUpdate(
    userToken: string,
    force: boolean = false
  ): Promise<FirmwareUpdateResponse> {
    const currentVersion = force
      ? '0/0/0/0'
      : this.deviceState
          .get()
          ?.versionFW?.replaceAll('.', '/')
          ?.replace('+', '/');
    if (!currentVersion) {
      logger.error("Couldn't detect current version on Bluetooth Freak");
      throw new Error("Couldn't detect current version on Bluetooth Freak");
    }

    const serverUrl = appData.displaySettings.serverUrl || SERVER_URL_ALPHA;

    // Firmware-Update-URL:
    const url = `${serverUrl}/firmware_update_from_version/${currentVersion}`;
    // // Make a request to the firmware server to see if there is a new version
    const config: AxiosRequestConfig = {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    let response: AxiosResponse<any, any>;
    try {
      response = await axios.get(url, config);
    } catch (e: any) {
      // If there is no data, we are up to date and can return early
      throw e;
    }
    if (response.status === 204)
      return { type: 'uptodate' } as FirmwareUpToDate;

    // If there is data, we need to convert it to base64 and return it wrapped
    // in a FirmwareUpdateAvailable object
    const contentDisp =
      response.headers['content-disposition'] || 'filename=unknownFirware.bin';
    const fileName = contentDisp.replaceAll('"', '').split('filename=')[1];
    const blob: Blob = response.data;
    const b64 = await blobToBase64(blob);
    return {
      type: 'updateavailable',
      fwData: b64,
      fileName,
    } as FirmwareUpdateAvailable;
  }

  async startUpdateProcess(
    fileUrl: string,
    statusCB?: (r: ZephyrResponse | Error) => void
  ): Promise<boolean> {
    logger.debug(`attempting to update ${fileUrl} on ${this.deviceId.get()}`);
    const result = await new Promise((resolve, _reject) => {
      this.setUserInputBle(StatesBLE.DISCONNECTED);
      this.disconnectFromSensor();
      if (!this.deviceId.get()) {
        logger.error('fsm - startUpdateProcess - No device ID');
        return resolve(new Error('No device ID'));
      }
      ZephyrDfu.updateFirmware(
        {
          fileURL: fileUrl,
          deviceIdentifier: this.deviceId.get() || '',
        },
        (response: any, e: any) => {
          const resp = response as unknown as ZephyrResponse;
          if (e) {
            logger.error('Update not successful:', e);
            statusCB?.(e);
            return resolve(e);
          }
          statusCB?.(resp);
          if (resp.status === 'upgradeCompleted') {
            logger.debug('Update successful:', resp.status);
            return resolve(resp);
          }
        }
      );
    });

    this.connectToSensor();
    return !isError(result);
  }

  async installFirmwareUpdate(
    userToken: string,
    statusCB?: (r: ZephyrResponse | Error) => void
  ): Promise<boolean> {
    try {
      const device = this.bleDevice.get()?.entry;
      if (this.bleState !== StatesBLE.CONNECTED || !device?.id) {
        logger.error('Update failed: Bluetooth not connected');
        statusCB?.(new Error('Update failed: Bluetooth not connected'));
        return false;
      }

      logger.debug('Device is connected and we have the device info');

      // Try to get the update binary, exit with error if no update is available
      const response = await this.checkForFirmwareUpdate(userToken);

      return this.updateFirmware(response, statusCB);
    } catch (err) {
      statusCB?.(err as Error);
      logger.debug('Update firmware error:', String(err));
    }
    return true;
  }

  async updateFirmware(
    response: FirmwareUpdateResponse,
    statusCB?: (r: ZephyrResponse | Error) => void
  ): Promise<boolean> {
    if (response.type === 'uptodate') {
      logger.debug('no update available');
      return false;
    }

    const fileInfo = {
      directory: Directory.Data,
      path: '',
    };
    // Save the binary to disk.
    // The filename is currently encoded into content-disposition in the format
    // 'attachment; filename="spinnax_freak_firmware_YYYY_MM_DD_HH_MM_SS.bin"'
    const { fwData, fileName } = response;
    fileInfo.path = fileName;

    let fileUrl = (await Filesystem.getUri(fileInfo)).uri;
    logger.info('fsm - updateFirmware - File URL:', fileUrl);
    const exists = await Filesystem.stat(fileInfo).catch<Error>(identity);

    if (isError(exists)) {
      logger.error('fsm - updateFirmware - File does not exist');
      const fileEntry = await Filesystem.writeFile({
        ...fileInfo,
        recursive: true,
        data: fwData,
      }).catch<Error>(identity);

      if (isError(fileEntry)) {
        logger.error(
          'fsm - updateFirmware - Error writing file:',
          fileEntry.message
        );
        statusCB?.(fileEntry);
        return false;
      }
    }
    return this.startUpdateProcess(fileUrl, statusCB);
  }

  // ----------------------------------------------------------------------------
  // State machine handling
  // ----------------------------------------------------------------------------

  public resetSamples() {
    logger.info('fsm - resetSamples - Resetting samples and sessions');
    this.recordSessions = [[], [], [], [], [], [], [], []];
    this.finalSamples = [];
    this.connectionLostCounter.set(0);
    this.recordSessionTimestamps = [[Date.now(), null]];
  }

  // The following enum is defined in embedded
  // enum {
  //  FsmMain_STATE_ROOT
  //  FsmMain_STATE_IDLE
  //  FsmMain_STATE_BUSY
  //  FsmMain_STATE_BUSY_MAINTAIN
  //  FsmMain_STATE_BUSY_RECORD
  // }
  private mapDongleStateToFSMState(state: number): FSMState | null {
    logger.info('fsm - mapDongleStateToFSMState - Mapping state:', state);
    switch (state) {
      case 1:
        return FSMState.Idle;
      case 2:
        return FSMState.Busy;
      case 3:
        return FSMState.Maintain;
      case 4:
        return FSMState.Record;
      default:
        return FSMState.Idle;
    }
  }

  // ----------------------------------------------------------------------------
  // BLE connection interfacing
  // ----------------------------------------------------------------------------

  findSensors(names: Array<string>): Subject<BluetoothEntry> {
    const subj = new ReplaySubject<BluetoothEntry>(1);
    let foundAnything = false;
    logger.info('fsm - findSensors - Searching for sensors with names:', names);
    if (this.lastKnown.entry) {
      this.ble.checkConnected(this.lastKnown.entry.id).then((v) => {
        if (v) foundAnything = true;
      });
    }
    const nameSet = new Set(names);
    this.ble.startScan([], this.debugStore.searchTime, {
      next: (entry: BluetoothEntry) => {
        logger.info(
          'fsm - findSensors - Found device:',
          entry.name,
          entry.id,
          entry.rssi
        );
        if (nameSet.has(entry.name) && entry.id) {
          foundAnything = true;
          subj.next(entry);
        }
      },
      error: (error) => {
        logger.error('fsm - findSensors - Scan error:', error, error.message);
        logger.debug('fsm - findSensors - scan failed');
      },
      complete: () => {
        if (!foundAnything)
          subj.error('fsm - findSensors - no new devices found');
        else subj.complete();
      },
    });
    return subj;
  }

  async connectToSensor(
    timeoutMs: number = this.TIMEOUT_CONNECT,
    retries = 0
  ): Promise<boolean> {
    logger.info('fsm - connectToSensor - Attempting to connect to sensor');
    runInAction(() => {
      this.setIsInitialConnection(true);
    });

    try {
      const result = await this._connectToSensor(timeoutMs);
      return result;
    } finally {
      // Nach dem Verbindungsversuch zurücksetzen
      setTimeout(() => {
        runInAction(() => {
          this.setIsInitialConnection(false);
        });
      }, this.TIMEOUT_IS_INITIAL_CONNECTION);
    }
  }

private async _connectToSensor(
  timeoutMs: number = 15000,
  retries = 0
): Promise<boolean> {
    // // Wenn wir bereits verbunden sind, zuerst trennen
    // if (this.bleState === StatesBLE.CONNECTED) {
    //   logger.info(
    //     'fsm - _connectToSensor - Already connected, disconnecting first'
    //   );
    //   await this.disconnectFromSensor();

    //   // Wichtig: Pause nach dem Trennen einfügen
    //   await new Promise((resolve) => setTimeout(resolve, this.PAUSE));
    // }

    // Wenn wir bereits verbinden, warten wir
    if (this.bleState === StatesBLE.CONNECTING) {
      logger.info(
        'fsm - _connectToSensor - Already connecting, waiting for result'
      );

      // Warten auf Statusänderung (max 15s)
      const result = await Promise.race([
        new Promise<boolean>((resolve) => {
          const disposer = observe(this.ble.connectionState, (change) => {
            if (change.newValue === StatesBLE.CONNECTED) {
              disposer();
              resolve(true);
            } else if (change.newValue === StatesBLE.DISCONNECTED) {
              disposer();
              resolve(false);
            }
          });

          // Timeout für die Beobachtung
          setTimeout(() => {
            disposer();
            resolve(false);
          }, timeoutMs);
        }),
        // Allgemeiner Timeout
        new Promise<boolean>((resolve) =>
          setTimeout(() => resolve(false), timeoutMs)
        ),
      ]);

      return result;
    }

    // Status auf CONNECTING setzen
    this.updateConnectionState(StatesBLE.CONNECTING);

    try {
      // Verbindung versuchen
      if (!this.deviceId.get()) {
        logger.warn('fsm - _connectToSensor - No device ID');
        return false;
      }
      const connectSubject = this.ble.autoConnect(
        this.deviceId.get() || '',
        timeoutMs
      );

      return await new Promise<boolean>((resolve) => {
        const subscription = connectSubject.subscribe({
          next: () => {
            logger.info(
              `fsm - _connectToSensor - Successfully connected to ${this.deviceId.get()}`
            );
            this.ble.connectionState.set(StatesBLE.CONNECTED);
            subscription.unsubscribe();
            resolve(true);
          },
          error: (err) => {
            logger.error(
              `fsm - _connectToSensor - Connection to ${this.deviceId.get()} failed:`,
              err
            );

            // Aufräumen und Status zurücksetzen
            this.updateConnectionState(StatesBLE.DISCONNECTED);
            subscription.unsubscribe();

            if (retries < 2) {
              // Neuer Versuch nach einer Pause
              setTimeout(() => {
                this.connectToSensor(timeoutMs, retries + 1)
                  .then(resolve)
                  .catch(() => resolve(false));
              }, this.PAUSE);
            } else {
              resolve(false);
            }
          },
          complete: () => {
            subscription.unsubscribe();
            resolve(this.bleState === StatesBLE.CONNECTED);
            logger.info(
              `fsm - _connectToSensor - Connection completed: ${this.deviceId.get()}`
            );
            // this.sampleSensorState().catch((e) => {
            //   logger.error('State check failed:', e);
            // });
          },
        });

        // Zusätzlicher Timeout für diese Promise
        setTimeout(() => {
          if (this.bleState === StatesBLE.CONNECTING) {
            logger.warn(`Connection attempt timed out in Promise wrapper`);
            this.updateConnectionState(StatesBLE.DISCONNECTED);
            subscription.unsubscribe();
            resolve(false);
          }
        }, timeoutMs + this.TIMEOUT_CONNECT_PROMISE);
      });
    } catch (error) {
      logger.error(
        `
        fsm - _connectToSensor - Error during connection attempt:` + error
      );
      this.updateConnectionState(StatesBLE.DISCONNECTED);
      return false;
    }
  }

  async disconnectFromSensor(): Promise<boolean> {
    if (this.deviceId.get()) {
      logger.warn('fsm - disconnectFromSensor - No device ID provided');
      return false;
    }
    const result = await this.ble.disconnect(this.deviceId.get()!);

    // Verwende die Action-Methode
    runInAction(() => {
      if (result) this.updateConnectionState(StatesBLE.DISCONNECTED);
    });
    return result;
  }

  private updateConnectionState = action(
    'updateConnectionState',
    (state: StatesBLE) => {
      logger.info(
        `fsm - updateConnectionState - Updating connection state to ${state}`
      );
      this.ble.connectionState.set(state);
    }
  );

  cancelConnection = action('cancelConnection', () => {
    if (this.bleState === StatesBLE.CONNECTING) {
      logger.warn('fsm - cancelConnection - Cancelling connection');
      const device = this.bleDevice.get();
      if (device?.entry?.id) {
        this.ble.disconnect(device.entry.id);
      }
      this.updateConnectionState(StatesBLE.DISCONNECTED);
    }
  });

  // ----------------------------------------------------------------------------
  // Getter und Setter
  // ----------------------------------------------------------------------------

  get bleState(): StatesBLE {
    return this.ble.connectionState.get();
  }

  get connectionLostCounterValue(): number {
    return this.connectionLostCounter.get();
  }

  get currentSession(): ST.RecordSample[] {
    return this.recordSessions[this.connectionLostCounter.get()] || [];
  }

  get recordSamples(): ST.RecordSample[] {
    // Flache Kopie aller Sessions als einzelnes Array
    return this.recordSessions.flat();
  }

  get getdeviceId(): null | string {
    return this.deviceId.get();
  }

  getUserInputState(): StatesUserInput {
    return this.userInputState.get();
  }

  getUserInputBle(): StatesBLE {
    return this.userInputBle.get();
  }

  getCanRecord(): boolean {
    return this.canRecord.get();
  }

  setUserInputState(state: StatesUserInput) {
    runInAction(() => this.userInputState.set(state));
  }

  setUserInputBle(state: StatesBLE) {
    runInAction(() => this.userInputBle.set(state));
  }

  setCanRecord(value: boolean) {
    runInAction(() => this.canRecord.set(value));
  }

  setIsFetchingState(value: boolean) {
    runInAction(() => this._isFetchingState.set(value));
  }

  setIsInitialConnection(value: boolean) {
    runInAction(() => this._isInitialConnection.set(value));
  }

  setDeviceId(value: string) {
    runInAction(() => this.deviceId.set(value));
  }

  setBatteryLevel(value: number | undefined) {
    runInAction(() => this.batteryLevel.set(value));
  }

  // ✅ ADD: Missing isUpdatingFirmware property
  private _isUpdatingFirmware: IObservableValue<boolean> = observable.box(
    false,
    {
      name: 'fsm.isUpdatingFirmware',
    }
  );

  // ✅ ADD: Getter für isUpdatingFirmware
  get isUpdatingFirmware(): boolean {
    return this._isUpdatingFirmware.get();
  }

  // ✅ ADD: Test-Methoden für lokale Firmware (Smartphone-kompatibel)
  async testLocalFirmwareUpdate(
    fileName: string = 'reset.bin',
    statusCB?: (r: ZephyrResponse | Error) => void
  ): Promise<boolean> {
    logger.info(
      'fsm - testLocalFirmwareUpdate - Starting local firmware test with:',
      fileName
    );

    try {
      runInAction(() => {
        this._isUpdatingFirmware.set(true);
      });

      const device = this.bleDevice.get()?.entry;
      if (this.bleState !== StatesBLE.CONNECTED || !device?.id) {
        logger.error(
          'fsm - testLocalFirmwareUpdate - Update failed: Bluetooth not connected'
        );
        statusCB?.(new Error('Update failed: Bluetooth not connected'));
        return false;
      }

      // ✅ FIXED: Korrekte Asset-URL für mobile Apps
      const assetUrl = `./assets/firmware/${fileName}`;

      logger.info('fsm - testLocalFirmwareUpdate - Fetching asset:', assetUrl);

      // ✅ Asset aus dem App-Bundle laden
      const response = await fetch(assetUrl);
      if (!response.ok) {
        throw new Error(
          `Failed to load test firmware: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      logger.info(
        'fsm - testLocalFirmwareUpdate - Loaded test firmware size:',
        uint8Array.length
      );

      // ✅ Debug-Info loggen
      const firstBytes = Array.from(uint8Array.slice(0, 16))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ');

      console.log('🔍 LOCAL FIRMWARE TEST - File:', fileName);
      console.log('🔍 LOCAL FIRMWARE TEST - Size:', uint8Array.length);
      console.log('🔍 LOCAL FIRMWARE TEST - First 16 bytes:', firstBytes);

      // ✅ Als Base64 konvertieren und temporär speichern
      const base64Data = await blobToBase64(blob);
      const tempFileName = `test_${fileName}`;

      // ✅ Datei im App-Storage speichern
      await Filesystem.writeFile({
        directory: Directory.Data,
        path: tempFileName,
        data: base64Data,
        recursive: true,
      });

      // ✅ File-URL für ZephyrDfu generieren
      let fileURL = (
        await Filesystem.getUri({
          directory: Directory.Data,
          path: tempFileName,
        })
      ).uri;

      if (fileURL.endsWith('/')) {
        fileURL = fileURL.slice(0, -1);
      }

      logger.info(
        'fsm - testLocalFirmwareUpdate - Using temp file URL:',
        fileURL
      );

      // ✅ Firmware-Update über DFU starten
      const result = await new Promise<boolean>((resolve) => {
        ZephyrDfu.updateFirmware(
          {
            fileURL: fileURL,
            deviceIdentifier: device.id,
          },
          (response: any, e: any) => {
            if (e) {
              logger.error('fsm - testLocalFirmwareUpdate - Update failed:', e);
              statusCB?.(e);
              return resolve(false);
            }

            const resp = response as unknown as ZephyrResponse;
            statusCB?.(resp);

            if (resp.status === 'upgradeCompleted') {
              logger.info(
                'fsm - testLocalFirmwareUpdate - Update completed successfully'
              );
              return resolve(true);
            }

            if (resp.status === 'upgradeFailed') {
              logger.error('fsm - testLocalFirmwareUpdate - Update failed');
              return resolve(false);
            }

            // Für andere Status-Updates einfach weiter warten
            return;
          }
        );
      });

      // ✅ Cleanup - Temp-Datei löschen
      try {
        await Filesystem.deleteFile({
          directory: Directory.Data,
          path: tempFileName,
        });
      } catch (cleanupError) {
        logger.warn(
          'fsm - testLocalFirmwareUpdate - Cleanup error:',
          cleanupError
        );
      }

      logger.info(
        'fsm - testLocalFirmwareUpdate - Test completed with result:',
        result
      );
      return result;
    } catch (error) {
      logger.error(
        'fsm - testLocalFirmwareUpdate - Error during local firmware test:',
        error
      );
      statusCB?.(error as Error);
      return false;
    } finally {
      // ✅ Update-Flag zurücksetzen
      runInAction(() => {
        this._isUpdatingFirmware.set(false);
      });
    }
  }
}

export const [FSMProvider, useFSM] = generateContext<FSMService>();
